import { apiSlice } from "../api-slice";
import { ComputationApprovalListResponse } from "../gms/proposal/proposal-api-state";
import { ActuarialApprovalHistory, ActuarialDepartmentListResponse, ActuarialDepartmentListResponseData, ComputationApprovalHistoryResponse, ComputationProposalResponse, DefaultActuarialResponse } from "./actuarial-api-response";
import { SubmitComputationToApproverBody } from "./acturial-body";

interface ComputationApproverListParams {
    page?: number;
    per_page?: number;
    status?: string;
    email?: string;
    group_name?: string;
    product?: string;
    preparedBy?: string;
    my?: boolean;
}

export const actuarialApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        downloadRatesTemplate: builder.mutation<Blob, { proposalId: number }>({
            query: ({ proposalId }) => ({
                url: `/api/actuarial/rates/${proposalId}/download-template`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformErrorResponse: (response: any) => response.data,
        }),
        uploadRatesExcel: builder.mutation<any, { proposalId: number; excelFile: File }>({
            query: ({ proposalId, excelFile }) => {
                const formData = new FormData();
                formData.append("excel_file", excelFile);
                return {
                    url: `/api/actuarial/rates/${proposalId}/upload-excel`,
                    method: "POST",
                    body: formData,
                };
            },
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "Proposal", id: proposalId },
            ],
        }),
        computeRates: builder.mutation<ComputationProposalResponse, { proposalId: number; body: any, method?: 'POST' | 'PUT' }>({
            query: ({ proposalId, body, method = 'POST' }) => ({
                url: `/api/actuarial/rates/${proposalId}`,
                method,
                body,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "Proposal", id: proposalId },
            ],
        }),
        computeEvidenceNotes: builder.mutation<ComputationProposalResponse, { proposalId: number; evidence_notes: string }>({
            query: ({ proposalId, evidence_notes }) => ({
                url: `/api/actuarial/evidence-notes/${proposalId}`,
                method: 'POST',
                body: { evidence_notes },
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "Proposal", id: proposalId },
            ],
        }),
        computeTotalAmountPremium: builder.mutation<ComputationProposalResponse, { proposalId: number; total_annual_premium: number }>({
            query: ({ proposalId, total_annual_premium }) => ({
                url: `/api/actuarial/total-premium/${proposalId}`,
                method: 'PUT',
                body: { total_annual_premium },
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "Proposal", id: proposalId },
            ],
        }),
        releaseProposal: builder.mutation<any, number>({
            query: (proposalId) => ({
                url: `/api/actuarial/release/${proposalId}`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, proposalId) => [
                { type: "Proposal", id: proposalId },
                { type: 'Proposal', id: 'LIST' }
            ],
        }),
        actuarialNotes: builder.mutation<ComputationProposalResponse, { proposalId: number; method: "POST" | "PUT", actuarial_notes: string, show_in_pdf: boolean }>({
            query: ({ proposalId, actuarial_notes, show_in_pdf, method }) => ({
                url: `/api/actuarial/notes/${proposalId}`,
                method,
                body: { actuarial_notes, show_in_pdf },
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "Proposal", id: proposalId },
                { type: 'Proposal', id: 'LIST' }
            ],
        }),
        actuarialNotesToCFE: builder.mutation<ComputationProposalResponse, { proposalId: number; method: "POST" | "PUT", notes: string }>({
            query: ({ proposalId, notes, method }) => ({
                url: `/api/actuarial/notes-to-cfe/${proposalId}`,
                method,
                body: { notes },
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "Proposal", id: proposalId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),

        getActuarialUserList: builder.query<ActuarialDepartmentListResponseData[], void>({
            query: () => ({
                url: `/api/actuarial/users/actuarial-list`,
            }),
            transformResponse: (response: ActuarialDepartmentListResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
        }),
        getComputationForApprovalHistory: builder.query<ActuarialApprovalHistory[], { proposalId: number }>({
            query: ({proposalId }) => `/api/actuarial/approval/${proposalId}/history`,
            transformResponse: (response: ComputationApprovalHistoryResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: (_result, _error, { proposalId }) => [
                { type: 'ComputationApprovalHistory', id: proposalId },
                { type: 'ComputationApprovalHistory', id: 'LIST' },
            ],
        }),
        getComputationForApprovalList: builder.query<ComputationApprovalListResponse, undefined | ComputationApproverListParams>({
            // query: (params) => ({
            //     url: "api/actuarial/approval/queue?my=true",
            //     params: params ?? {},
            // }),
            query: (params) => {
                const { my, ...queryParams } = params ?? {};

                return {
                    url: "api/actuarial/approval/queue",
                    params: {
                        ...queryParams,
                        ...(my !== undefined
                            ? { my: my ? "true" : "false" }
                            : {}),
                    },
                };
            },
            transformResponse: (response: ComputationApprovalListResponse) => response,
            transformErrorResponse: (response: any) => response.data,
            providesTags: [{ type: "ComputationApproval", id: "LIST" }],
        }),
        updateComputationApproval: builder.mutation<DefaultActuarialResponse, { proposalId: number; notes?: string; type: 'deny' | 'pass' }>({
            query: ({ proposalId, notes, type }) => ({
                url: `/api/actuarial/approval/${proposalId}/${type}`,
                method: 'PUT',
                body: { ...(notes?.trim() ? { notes: notes.trim() } : {}) },
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: "ComputationApproval", id: proposalId },
                { type: 'ComputationApproval', id: 'LIST' },
                { type: "Proposal", id: proposalId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),
        submitComputationToApprover: builder.mutation<DefaultActuarialResponse, { proposalId: number; body: SubmitComputationToApproverBody }>({
            query: ({proposalId, body}) => ({
                url: `api/actuarial/approval/${proposalId}/request`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, {proposalId}) => [
                { type: "ComputationApproval", id: proposalId },
                { type: 'ComputationApproval', id: 'LIST' },
                { type: "Proposal", id: proposalId },
                { type: 'Proposal', id: 'LIST' },
                { type: 'ComputationApprovalHistory', id: proposalId },
                { type: 'ComputationApprovalHistory', id: 'LIST' },
            ],
        }),
    })
})

export const {
    useUploadRatesExcelMutation,
    useDownloadRatesTemplateMutation,
    useComputeRatesMutation,
    useComputeEvidenceNotesMutation,
    useComputeTotalAmountPremiumMutation,
    useReleaseProposalMutation,

    useActuarialNotesMutation,
    useActuarialNotesToCFEMutation,

    useGetActuarialUserListQuery,
    useGetComputationForApprovalHistoryQuery,
    useGetComputationForApprovalListQuery,
    useUpdateComputationApprovalMutation,
    useSubmitComputationToApproverMutation,
} = actuarialApi;